import { ChangeDetectorRef, Component, Input, OnChanges, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../../dynamic-forms/inputs/selectable-input.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { TextboxType } from "../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { NumberHelper } from "../../../../../utilities/contracts/number-helper";
import { RegExHelper } from "../../../../../utilities/reg-Ex-Helper";
import { DirectoryUser } from "../models/directory-user.model";
import { ProjectConfigurationService } from "../project-config.service";

interface IUserForm {
  formGroup: FormGroup;
  userModels: DynamicInput[];
}

interface IUserSamplingModel {
  active: number;
  user: IUserSamplingUser[];
}

interface IUserSamplingUser {
  userid: number;
  samplingtype: string;
  value: any;
}

@Component({
  selector: "app-user-sampling-overread",
  templateUrl: "./user-sampling-overread.component.html",
  styleUrls: ["./user-sampling-overread.component.scss"],
})
export class UserSamplingOverreadComponent implements OnInit, OnChanges {

  @Input() projectId: number;
  @Input() qaRates: any;

  defaultUser = "Project User Default";
  userSamplingSwitchGroup: FormGroup;
  userRowGroup: IUserForm[] = [];
  userOptions: SelectableInput[] = [];
  userSamplingEnabled = false;

  constructor(private readonly formService: FormService, private readonly changeDetector: ChangeDetectorRef, private service: ProjectConfigurationService) {
    this.userRowGroup = [];
  }

  get users(): IUserSamplingModel {
    return this.qaRates?.overread?.users;
  }

  get userSamplingActive(): boolean {
    return this.userSamplingActiveSwitch.value === 1;
  }

  userSamplingActiveSwitch = new Switch({
    key: "active",
    tooltip: "This setting requires coders to review and acknowledge user Sampling",
    value: false,
  });

  onUserSamplingSwitchChange(event: any): void {
    if (event && event.checked !== undefined) {
      this.userSamplingActiveSwitch.value = event.checked ? 1 : 0;
      this.userSamplingEnabled = event.checked;
      this.changeDetector.markForCheck();
    }
  }

  getValuesAsObject(): IUserSamplingModel {
    const val = this.getFormattedValues(this.userRowGroup);
    return { active: this.userSamplingEnabled ? 1 : 0, user: val };
  }

  getFormattedValues(form: IUserForm[]): any[] {
    const users = [];
    form.forEach((userForm, _) => {
      const samplingType = userForm.formGroup.get("samplingType")?.value;
      const fullNameValue = userForm.formGroup.get("fullName")?.value;
      const samplingCount = userForm.formGroup.get("samplingCount")?.value;

      const userObj: any = {
        userId: fullNameValue === this.defaultUser ? null : fullNameValue,
        samplingtype: samplingType,
        value: samplingCount,
      };

      users.push(userObj);
    });
    return users;
  }

  ngOnInit(): void {
    this.userSamplingSwitchGroup = this.formService.createFormGroup([this.userSamplingActiveSwitch]);
  }

  ngOnChanges(): void {
    if (this.qaRates) {
      this.userSamplingSwitchGroup?.patchValue({ active: this.users?.active });

      this.service.getProjectUsers(this.projectId).subscribe((users: DirectoryUser[]) => {
        this.userOptions = [
          new SelectableInput({ text: this.defaultUser, value: this.defaultUser }),
          ...users.map(u => new SelectableInput({ text: `${u.firstName} ${u.lastName}`, value: u.userId })),
        ];

        if (this.qaRates) {
          this.loadUserRows();

          if (this.users?.user?.length === this.userRowGroup.length) {
            this.userRowGroup.forEach((group, idx) => {
              const user = this.users.user[idx];
              group.formGroup.patchValue({
                [`fullName`]: user.userid ?? this.defaultUser,
                [`samplingType`]: user.samplingtype,
                [`samplingCount`]: user.value,
              });
            });
          }
          this.userSamplingSwitchGroup?.patchValue({ active: this.users?.active === 1 ? true : false });
          this.userSamplingEnabled = this.users?.active === 1;
        }
      });
    }
  }

  addNewRow(): void {
    if (!this.userSamplingEnabled) {
      return;
    }
    const newUser: IUserSamplingUser = {
      userid: 0,
      samplingtype: "",
      value: "",
    };
    const controls = this.createUserRow(newUser);

    this.userRowGroup.push({ formGroup: this.formService.createFormGroup(controls), userModels: controls });
    this.changeDetector.markForCheck();
  }

  deleteRow(index: number): void {
    if (index > -1 && index < this.userRowGroup.length) {
      this.userRowGroup.splice(index, 1);

      if (this.qaRates?.overread?.users && this.qaRates.overread.users.length > index) {
        this.qaRates.overread.users.user.splice(index, 1);
      }
      this.changeDetector.markForCheck();
    }
  }

  loadUserRows(): void {
    if (this.users?.user?.length > 0) {
      this.users?.user.forEach((user: IUserSamplingUser) => {
        const controls = this.createUserRow(user);
        this.userRowGroup.push({
          formGroup: this.formService.createFormGroup(controls),
          userModels: controls,
        });
      });
    }
  }

  formHasErrors(): boolean {
    return this.userRowGroup.some(form => form.formGroup.invalid);
  }

  getControls(form: FormGroup): AbstractControl[] {
    const controls: AbstractControl[] = [];
    for (const [key, control] of Object.entries(form.controls)) {
      controls.push(control);
    }
    return controls;
  }

  canDeleteRow(group: IUserForm): boolean {
    return group !== null && group.formGroup.get("fullName")?.value !== this.defaultUser;
  }

  createUserRow(user: IUserSamplingUser): DynamicInput[] {
    return [
      this.createDropDown(`fullName`, "Select user", this.userOptions, !NumberHelper.isAvailable(user.userid)),
      this.createDropDown(`samplingType`, "Select type", [
        new SelectableInput({ text: "Percentage", value: "Percentage" }),
        new SelectableInput({ text: "Count per week", value: "CountPerWeek" }),
      ]),
      this.createTextbox(`samplingCount`, "", TextboxType.NUMBER),
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }

  private createDropDown(key: string, placeholder: string, options: SelectableInput[], disabled: boolean = false): Dropdown {
    return new Dropdown({
      key,
      placeholder: "Select type",
      options: options || [],
      disabled,
      label: "",
      validators: [Validators.required],
    });
  }

  private createTextbox(key: string, label: string, textBoxType: TextboxType = TextboxType.TEXT): Textbox {
    return new Textbox(
      {
        key,
        label,
        type: textBoxType || null,
        validators: [Validators.required, Validators.pattern(RegExHelper.wholeNumber)],
        errorMessages: {
          required: "This field is required.",
          pattern: "Please enter a valid number.",
        },
      });
  }
}
